import { useEffect } from "react";

import { showApiErrorToast } from "../utils/showApiErrorToast";
import {
  RATE_LIMIT_EVENT,
  rateLimitToastMessage,
  type RateLimitEventDetail,
} from "../utils/httpRateLimit";
import { isDemoEntryAnalysisInflightSlug } from "../utils/demoEntryAnalysisErrors";
import {
  isSimulateInflightErrorSlug,
  simulateInflightToastCopy,
} from "../utils/simulateQuotaToasts";

/** Shows a single toast when any API call receives HTTP 429. */
export function RateLimitToastListener(): null {
  useEffect(() => {
    const onRateLimited = (event: Event) => {
      const detail = (event as CustomEvent<RateLimitEventDetail>).detail;
      if (!detail) return;

      if (isSimulateInflightErrorSlug(detail.errorSlug)) {
        const forReport = Boolean(detail.endpoint?.includes("/reports/"));
        const copy = simulateInflightToastCopy({
          forReport,
          backendMessage: detail.message,
        });
        showApiErrorToast({
          title: copy.title,
          description: copy.description,
          status: "warning",
        });
        return;
      }

      if (isDemoEntryAnalysisInflightSlug(detail.errorSlug)) {
        showApiErrorToast({
          title: "Report already preparing",
          description:
            detail.message?.trim() ||
            "You already have a demo report preparing. Continue to that report, or wait for it to finish.",
          status: "info",
        });
        return;
      }

      if (!detail.retryAfterSeconds) return;
      const fallback = rateLimitToastMessage(detail.retryAfterSeconds);
      showApiErrorToast({
        title: "Rate limit reached",
        description: detail.message?.trim() || fallback,
        status: "warning",
        duration: Math.min(detail.retryAfterSeconds * 1000, 8000),
      });
    };
    window.addEventListener(RATE_LIMIT_EVENT, onRateLimited);
    return () => window.removeEventListener(RATE_LIMIT_EVENT, onRateLimited);
  }, []);

  return null;
}
